import React from 'react';
import { Flex, Form } from 'antd';
import { CustomTitle, ToggleSwitch } from '@/components';
import TagList from './TagList/TagList';
import type { AlertSettingsFormValues } from './alertSettingsForm';

type RequiredTagType = 'assignment' | 'resolution' | 'dismissal';

const REQUIRE_TOGGLE_LABELS: Record<RequiredTagType, string> = {
  assignment: 'Require a tag when assigning an alert',
  resolution: 'Require a tag when closing an alert',
  dismissal: 'Require a tag when closing an alert',
};

/** Error shown when a list is empty while its "Require a tag" toggle is on. */
export const getRequiredTagMessage = (type: RequiredTagType): string =>
  `Add at least one ${type} tag. "${REQUIRE_TOGGLE_LABELS[type]}" is on, so users need a tag to pick. Add a tag or turn the toggle off.`;

const labelStyle = { fontWeight: 600, color: 'var(--text-primary)' } as const;

/** Heading that sits above a bordered group, outside its left border. */
const GroupTitle: React.FC<{ text: string }> = ({ text }) => (
  <span style={labelStyle}>{text}</span>
);

interface ToggleFieldProps {
  value?: boolean;
  onChange?: (value: boolean) => void;
}

/** Form.Item-compatible wrapper so toggles need no hidden mirror field. */
const ToggleField: React.FC<ToggleFieldProps> = ({ value, onChange }) => (
  <ToggleSwitch
    alignLeft
    checked={!!value}
    onChange={(event) => onChange?.(event.target.checked)}
  />
);

const SettingToggle: React.FC<{ name: string; label: string }> = ({
  name,
  label,
}) => (
  <Flex align='center' gap={12}>
    <Form.Item name={name} noStyle>
      <ToggleField />
    </Form.Item>
    <span style={labelStyle}>{label}</span>
  </Flex>
);

// Reads the surrounding <Form> from context, so the page can keep its own
// form-values type without matching AlertSettingsFormValues exactly.
const AlertSettings: React.FC = () => {
  const form = Form.useFormInstance<AlertSettingsFormValues>();
  // preserve: the require toggles mount only after assignment is switched on,
  // and useWatch skips unregistered fields unless preserve is set.
  const assignmentEnabled = Form.useWatch('owner_assignment_enabled', {
    form,
    preserve: true,
  });
  const requireAssignmentTag = Form.useWatch('require_assignment_tag', {
    form,
    preserve: true,
  });
  const requireResolutionTag = Form.useWatch('require_resolution_tag', {
    form,
    preserve: true,
  });
  const requireDismissalTag = Form.useWatch('require_dismissal_tag', {
    form,
    preserve: true,
  });

  return (
    <Flex vertical gap={32}>
      <Flex vertical gap={4}>
        <CustomTitle
          text='Alert settings'
          fontSize='16px'
          fontWeight='600'
          lineHeight='20px'
          color='var(--text-primary)'
          marginBottom='0'
        />
        <CustomTitle
          text='Choose whether alerts can be assigned to an owner, and define the tags your team picks when reassigning, resolving or dismissing them'
          fontSize='14px'
          fontWeight='400'
          lineHeight='20px'
          color='var(--text-secondary)'
          marginTop='0'
          marginBottom='0'
        />
      </Flex>

      <SettingToggle
        name='owner_assignment_enabled'
        label='Enable alert owner assignment'
      />

      {assignmentEnabled ? (
        <Flex
          vertical
          gap={16}
          style={{ paddingLeft: 15, borderLeft: '2px solid #E8E8E8' }}
        >
          <TagList
            name='assignment_tags'
            deleteLabel='Delete assignment tag'
            required={!!requireAssignmentTag}
            requiredMessage={getRequiredTagMessage('assignment')}
          />
          <SettingToggle
            name='require_assignment_tag'
            label='Require a tag when assigning an alert'
          />
        </Flex>
      ) : null}

      <Flex vertical gap={12}>
        <GroupTitle text='Alert resolutions' />
        <Flex
          vertical
          gap={16}
          style={{ paddingLeft: 14, borderLeft: '2px solid #E8E8E8' }}
        >
          <TagList
            name='resolution_tags'
            deleteLabel='Delete resolution tag'
            required={!!requireResolutionTag}
            requiredMessage={getRequiredTagMessage('resolution')}
          />
          <SettingToggle
            name='require_resolution_tag'
            label='Require a tag when closing an alert'
          />
        </Flex>
      </Flex>
      <Flex vertical gap={12}>
        <GroupTitle text='Alert dismissals' />
        <Flex
          vertical
          gap={16}
          style={{ paddingLeft: 14, borderLeft: '2px solid #E8E8E8' }}
        >
          <TagList
            name='dismissal_tags'
            deleteLabel='Delete dismissal tag'
            required={!!requireDismissalTag}
            requiredMessage={getRequiredTagMessage('dismissal')}
          />
          <SettingToggle
            name='require_dismissal_tag'
            label='Require a tag when closing an alert'
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AlertSettings;
